import { createFileRoute } from "@tanstack/react-router";
import { useI18n } from "@/lib/i18n";
import { translatePortuguese } from "@/data/portuguese-core";
import { localizedCoreHead } from "@/lib/core-head";

import { ProjectCarousel } from "@/components/project-carousel";
import { SiteFooter } from "@/components/site-footer";
import { HomeNav } from "@/components/home-nav";
import { portraits } from "@/data/about";
import portraitEye from "@/assets/luiza-portrait-eye.webp.asset.json";
import artistPresentation from "@/assets/artist-presentation.mp4.asset.json";
import artistPresentationPoster from "@/assets/artist-presentation-poster.jpg.asset.json";
import portraitLudmilaRusso from "@/assets/luiza-portrait-ludmila-russo.webp.asset.json";
import { VideoEmbed } from "@/components/video-embed";

const bio = [
  "Luiza Jesus do Prado, also known as MAVRA (former Hifa Cybe, Poteh Pehuono and Luiza Prado), is a Brazilian transdisciplinary artist and researcher. Her work articulates art, science, and technology to investigate memory, mental health, and trauma, exploring expanded photography, sound, and performance. Her bodily practices engage with ritual, gender, and decolonial healing processes.",
  "Luiza Jesus do Prado is a multidisciplinary researcher in Biomedical Science areas focused on Biophysics, Semiotics, Neuroscience, and Psychobiology. With academic training in Multimedia, Photography, and Biomedical Sciences, she develops research at the intersection of Mental Health, Public Health, and the Arts. She is currently candidate a postgraduate student in Psychopathology and Public Health at Santa Casa and holds certifications from institutions such as Harvard, Johns Hopkins, HCFMUSP, and Bayreuth in areas including Neurogenetics, Neuroimaging using fMRI, Genomic Data Analysis, and Biofabrication.",
  "Her interests encompass Genetics, Molecular Biology, Pharmacology, Health Tech, Immunology, Toxicology, and Integrative and Complementary Health Practices. She explores experimental and transdisciplinary methodologies that articulate Science and Art to create accessible and non-bureaucratic approaches to mental health treatment and harm reduction. Prado also conducts parallel studies in fields such as Mycology, Nuclear Physics, Anthropology, Acoustics, Wave Theory, and Philosophy.",
  "I define my practice as transdisciplinary, guided by integration and the search for meaning at the intersection of art and science. Within an open and indeterminate horizon, these fields converge, organize themselves, and merge, keeping the body as a central agent, moving between macro and micro scales, and between the public and the private.",
  "The body becomes essential within an aesthetic shaped by ancestral rituals and symbolic systems, remaining in direct contact with the materials it records, as well as with substances that provoke denial, discomfort, or repulsion. This approach constitutes a mechanism for developing an alternative, visceral, marginalized, and expanded aesthetic, often organic, processed through both traditional and technical platforms. Fluids, blood, residues, oxidation, putrefaction, and recycling operate as temporal and material processes within the work.",
  "Themes such as sexuality and gender, decolonization, peripheral realities, the deconstruction of privilege, syncretism, Indigenous ancestry, politics, memory, and mental health permeate my practice, emerging directly from my biography. What began as a process of self-knowledge expanded into an ethical necessity to be useful, to place my body in a political state of availability. Like madness, art was not a choice but a necessity, a form of resistance and liberation, and a method of reprocessing experience through multiple perspectives, without limits to development, questioning, or inclusion.",
  "Her scientific research begins with questions regarding the consolidation and recovery of memory through photographic reprocessing, image decompilation, and sounds generated from visual data. These processes investigate memory as unstable, fragmented, stratified, and in constant rewriting.",
  "Her artistic practice merges with scientific research, functioning as a living laboratory in which art materializes, translates, and destabilizes scientific concepts. In this space, art becomes a tool for experimentation, reflection, and the democratization of methods traditionally restricted to clinical or academic psychological treatment, opening them to broader sensory and cultural discourses.",
  "Her research focuses on neurophysiological responses to sensory stimuli, particularly sound and image, examining their construction, distortion, and physical and chemical reactions in the human brain. These investigations encompass photographic reprocessing, the decoding of communication systems, image decompilation, visual effects induced by chemical agents such as those associated with psilocybin and reenacted through artistic processes, and performance as a central device for trauma desensitization.",
  "Developed through a transdisciplinary methodology, her practice operates at the intersection of semiotics, neuroscience, and psychobiology, engaging with biochemistry, physics, genetics, pharmacology, and data science. This hybrid environment sustains a field of research in which scientific investigation and artistic experience coexist, influence one another, and expand mutually."
];

const facts = [
  "Person with a disability (PwD); autistic.",
  "Trans person; agender.",
];

export const Route = createFileRoute("/about")({
  head: ({ matches }) => localizedCoreHead("/about", matches[0]?.context.getLang() ?? "en", {
    meta: [
      { property: "og:url", content: "https://www.luizajesusdoprado.com/about" },
      { title: "About — Luiza Jesus do Prado" },
      {
        name: "description",
        content:
          "Biography of Luiza Jesus do Prado, Brazilian political artist and researcher in art, science and technology, mental health, psilocybin, ritual, decolonization and Indigenous knowledge.",
      },
      { property: "og:title", content: "About — Luiza Jesus do Prado" },
      {
        property: "og:description",
        content:
          "Brazilian artist working across art, science and technology, mental health, psilocybin research, ritual and decolonial practice.",
      },
      { property: "og:type", content: "profile" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:image", content: portraits[0]?.src ?? "" },
      { name: "twitter:image", content: portraits[0]?.src ?? "" },
    ],
  }),
  component: About,
});

function About() {
  const { lang } = useI18n();
  const tr = (text: string) => translatePortuguese(text, lang);
  const carousel = portraits.slice(1, 13);

  return (
    <div className="min-h-screen bg-ink text-bone">
      <HomeNav />

      <main id="main-content" className="pt-28 md:pt-36">
        <h1 className="sr-only">{tr("About Luiza Jesus do Prado")}</h1>
        {/* Lead */}
        <section className="mx-auto max-w-[1600px] border-b border-bone/15 px-6 py-16 md:px-10 md:py-24">
          <figure className="mx-auto w-full max-w-[380px] md:max-w-[460px]">
            <img
              src={portraitLudmilaRusso.url}
              alt={tr("Portrait of Luiza Jesus do Prado by Ludmila Russo")}
              className="h-auto w-full object-contain"
            />
            <figcaption className="mt-3 text-center font-mono text-[10px] uppercase tracking-[0.24em] text-bone/65">{tr("Photo by Ludmila Russo")}</figcaption>
          </figure>
          <div className="mt-20 grid grid-cols-12 gap-x-6 gap-y-10 md:mt-32">
            <p className="col-span-12 font-display text-[clamp(1.5rem,3.2vw,2.75rem)] leading-[1.1] tracking-tight md:col-span-9">{tr("Luiza Jesus do Prado, also known as")}{" "}
              <a href="https://www.mavrayvyuna.com" target="_blank" rel="noopener noreferrer" className="underline decoration-bone/30 underline-offset-4 hover:decoration-bone">{tr("MAVRA")}</a>{" "}{tr("(former Hifa Cybe, Poteh Pehuono and Luiza Prado)")}{tr((bio[0] ?? "").slice((bio[0] ?? "").indexOf(")") + 1))}
            </p>
          </div>
        </section>

        {/* MIDDLE — white text overlays on a dark portrait */}
        <section className="relative w-full overflow-hidden">
          <img
            src={portraitEye.url}
            alt={tr("Close-up black and white portrait of Luiza Jesus do Prado")}
            className="h-auto max-h-[100vh] w-full bg-ink object-contain"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-ink/70 via-ink/25 to-ink" />
          <div className={`${lang === "pt" ? "relative md:absolute" : "absolute"} inset-0 mx-auto grid max-w-[1600px] grid-cols-12 content-between gap-x-6 gap-y-8 px-6 py-14 md:px-10 md:py-20`}>
            <p className="col-span-12 font-mono text-[10px] uppercase tracking-[0.3em] text-bone/70 md:col-span-4">{tr("01 — Scientific research")}</p>
            <p className="col-span-12 max-w-xl text-sm leading-relaxed text-bone/85 md:col-span-5 md:col-start-8">
              {tr(bio[1] ?? "")}
            </p>
            <p className="col-span-12 mt-auto max-w-xl text-sm leading-relaxed text-bone/85 md:col-span-5">
              {tr(bio[2] ?? "")}
            </p>
            <p className="col-span-12 mt-auto self-end text-right font-display text-[clamp(2rem,6vw,5rem)] leading-[0.85] md:col-span-5 md:col-start-8">{tr("Memory")} <span className="text-red">{tr("as")}</span> {tr("matter")}</p>
          </div>
        </section>

        {/* Body + carousel of portraits (as the original site) */}
        <section className="mx-auto max-w-[1600px] px-6 py-32 md:px-10 md:py-48">
          <div className="grid grid-cols-12 gap-x-6 gap-y-10">
            <p className="col-span-12 font-mono text-[10px] uppercase tracking-[0.3em] text-bone/70 md:col-span-2">{tr("02 — Personal statement")}</p>
            <div className="col-span-12 space-y-6 text-base leading-relaxed text-bone/75 md:col-span-5">
              <p>{tr(bio[3] ?? "")}</p>
              <p>{tr(bio[4] ?? "")}</p>
              <p>{tr(bio[5] ?? "")}</p>
              <h2 className="pt-6 font-mono text-[10px] uppercase tracking-[0.3em] text-red">{tr("Her story")}</h2>
              <p>{tr("My motivation to become an artist is rooted in my own story. I am a patient, from a family crossed by psychic suffering and racial issues — my maternal family is Indigenous. Art became a way to reprocess this inheritance and transform it into research, resistance and care.")}</p>
              <h2 className="pt-6 font-mono text-[10px] uppercase tracking-[0.3em] text-red">{tr("Poetics")}</h2>
              <p>{tr(bio[6] ?? "")}</p>
              <p>{tr(bio[7] ?? "")}</p>
              <p>{tr(bio[8] ?? "")}</p>
              <p>{tr(bio[9] ?? "")}</p>
            </div>
            <div className="col-span-12 space-y-6 text-sm leading-relaxed text-bone/60 md:col-span-4 md:col-start-9">
              <figure className="mx-auto w-full max-w-[360px] md:mx-0 md:max-w-none">
                <VideoEmbed
                  title={tr("Artist presentation")}
                  file={artistPresentation.url}
                  poster={artistPresentationPoster.url}
                  aspect="aspect-[9/16]"
                />
              </figure>
              <div>
                <h2 className="font-mono text-[10px] uppercase tracking-[0.3em] text-red">{tr("Scientific resume")}</h2>
                <p className="mt-4">{tr("Multidisciplinary researcher focused on Biophysics, Semiotics, Neuroscience and Psychobiology. Academic training in Multimedia, Photography and Biomedical Sciences; postgraduate student in Psychopathology and Public Health at Santa Casa, with certifications from Harvard, Johns Hopkins, HCFMUSP and Bayreuth in Neurogenetics, fMRI Neuroimaging, Genomic Data Analysis and Biofabrication.")}</p>
              </div>
              <div>
                <h2 className="font-mono text-[10px] uppercase tracking-[0.3em] text-red">{tr("Artistic resume")}</h2>
                <p className="mt-4">{tr("Expanded photography, sound and performance articulating ritual, gender and decolonial healing. Exhibited at PST: LA/LA (USA), Casablanca Biennale (Morocco) and the Shanghai Biennial of Photography and Video, with works at MAC Bogotá.")}</p>
                <a
                  href="https://www.dropbox.com/scl/fi/77s0f2dpgvok01p63dvgm/eng_Curriculum_Luiza_Jesus_do_Prado.pdf?rlkey=xiq6fctn2fxu9bvaxtw71ejkd&dl=0"
                  target="_blank"
                  rel="noreferrer"
                  className="mt-5 inline-block border-b border-red pb-1 font-mono text-[10px] uppercase tracking-[0.24em] text-bone transition-opacity hover:opacity-60"
                >{tr("Download full CV (EN)")}</a>
              </div>
            </div>
          </div>

          <div className="mt-32 md:mt-56">
            <ProjectCarousel images={carousel} contain />
          </div>
        </section>

        {/* END — red poster with small centered image inset */}
        <section className="bg-red-deep px-6 py-20 md:px-10 md:py-28">
          <div className="mx-auto max-w-[1400px] text-center">
            <p className="font-mono text-[10px] uppercase tracking-[0.32em] text-bone/70">{tr("03 — Statement")}</p>
            <h2 className="mt-8 font-display text-[clamp(2.5rem,9vw,8rem)] leading-[0.82] text-ink">{tr("Decolonize")}</h2>
            <div className="mx-auto my-6 w-[46%] max-w-[420px]">
              {portraits[5] ? (
                <img
                  src={portraits[5].src}
                  alt={portraits[5].alt}
                  className="w-full object-cover contrast-125"
                />
              ) : null}
            </div>
            <h2 className="font-display text-[clamp(2.5rem,9vw,8rem)] leading-[0.82] text-ink">{tr("Mental health")}</h2>
            <p className="mx-auto mt-10 max-w-2xl text-sm leading-relaxed text-bone/85">{tr("Emotional, spiritual and sociopolitical layers held together — art as an accessible, non-bureaucratic protocol for treatment, harm reduction and memory.")}</p>
            <ul className="mx-auto mt-16 max-w-xl space-y-3 border-t border-ink/20 pt-6 font-mono text-[10px] uppercase leading-relaxed tracking-[0.16em] text-bone/60">
              {facts.map((fact) => (
                <li key={fact.slice(0, 24)}>{tr(fact)}</li>
              ))}
            </ul>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}
